import React, { useState, useEffect } from "react";
import "../styles/AssignCandidates.scss";
import {
  getAllSkills,
  getCandidatesForAssignment,
  base_URL,
  assignCandidates,
} from "../services/api";
import { useNavigate, useLocation } from "react-router-dom";
import Select from "react-select";
import Swal from "sweetalert2";
import { AiOutlineEye } from "react-icons/ai";

function AssignCandidate() {
  const [accordionOpen, setAccordionOpen] = useState(false);
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [requisition, setRequisition] = useState(null);
  const [searchName, setSearchName] = useState("");
  const [searchSkills, setSearchSkills] = useState([]);
  const [skillsOptions, setSkillsOptions] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const PAGE_SIZE = 10;
  const roleId = localStorage.getItem("role_id");
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch skills
        const skillsResponse = await getAllSkills();
        if (
          skillsResponse &&
          skillsResponse.result_code === 200 &&
          Array.isArray(skillsResponse.result_info)
        ) {
          const skills = skillsResponse.result_info.map(
            (skill) => skill.skill_name || skill.name || skill
          );
          setSkillsOptions(skills);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, []);

  // 🔍 Function to fetch candidates and associated requisition data
  const fetchCandidatesData = async () => {
    const jobId = location.state?.jobId;

    const requisitionState = location.state?.requisition || {};
    console.log("Requisition state:", requisitionState);

    // Prepare API call if jobId is provided
    if (jobId) {
      setLoading(true);
      try {
        const payload = {
          requisition_id: jobId,
        };
        const response = await getCandidatesForAssignment(payload);

        if (response && response.result_code === 200 && response.result_info) {
          setRequisition({
            correl_id: requisitionState?.correl_id || "",
            jobTitle:
              requisitionState?.jobTitle ||
              requisitionState?.job_title ||
              "Unknown Job",
            location: requisitionState?.location || "",
            department: requisitionState?.department || "",
            hiringManager:
              requisitionState?.hiringManager ||
              requisitionState?.recruiters ||
              "Unknown",
            openings: requisitionState?.openings || 0,
            description:
              requisitionState?.description ||
              requisitionState?.job_desc ||
              "No description available",
            skills: requisitionState?.skills || "",
            candidatesCount: response.result_info.total_matching || 0,
            total_matching: response.result_info.total_matching || 0,
            availableForAssignment:
              response.result_info.available_for_assignment || 0,
          });

          setCandidates(
            (response.result_info.candidates || []).map((candidate) => ({
              id: candidate.id,
              fullName: candidate.candidate_name,
              name: candidate.candidate_name,
              email: candidate.email,
              phone: candidate.phone,
              experience: candidate.years_of_exp,
              linkedin: candidate.linkedin_url,
              location: candidate.location,
              skills: candidate.skills
                ?.split(/,|\s|\n/)
                .map((s) => s.trim())
                .filter(Boolean),
              resumeUrl: candidate.resume_path
                ? `${
                    process.env.REACT_APP_API_URL || base_URL
                  }files/resume/${candidate.resume_path.replace(/\\/g, "/")}`
                : null,
              notes: candidate.additional_notes || "",
              presentCtc: candidate.present_ctc,
              expectedCdc: candidate.expected_cdc,
              noticePeriod: candidate.notice_period,
              assigned:
                candidate.assigned === true ||
                candidate.assigned === "true" ||
                candidate.status === "assigned" ||
                false,
              status: candidate.status || "New",
            }))
          );

          return; // Exit early on success
        }
      } catch (error) {
        console.error("Error fetching candidates:", error);
        Swal.fire({
          icon: "error",
          title: "Fetch Failed",
          text: "Could not retrieve candidate data. Please try again.",
          confirmButtonColor: "#d33",
        });
      } finally {
        setLoading(false);
      }
    }

    // ⛔ Fallback: Use requisition data from navigation state
    if (requisitionState) {
      console.log("Using requisition data from state:", requisitionState);
      setRequisition({
        correl_id: requisitionState.correl_id || "",
        jobTitle:
          requisitionState.jobTitle ||
          requisitionState.job_title ||
          "Unknown Job",
        location: requisitionState.location || "",
        department: requisitionState.department || "",
        hiringManager:
          requisitionState.hiringManager ||
          requisitionState.recruiters ||
          "Unknown",
        openings: requisitionState.openings || 0,
        description:
          requisitionState.description ||
          requisitionState.job_desc ||
          "No description available",
        skills: requisitionState.skills || "",
        candidatesCount: 0,
      });
    } else {
      // ⛔ Final fallback if no state passed
      setRequisition({
        jobTitle: "No Job Selected",
        location: "Unknown",
        department: "Unknown",
        hiringManager: "Unknown",
        openings: 0,
        description: "No job requisition data available",
        skills: [],
        candidatesCount: 0,
      });
    }

    // Ensure candidates are cleared on failure or no data
    setCandidates([]);
  };

  // 🚀 Effect to run on component mount or location state change
  useEffect(() => {
    fetchCandidatesData();
  }, [location.state]);

  // Filter candidates by name and skills
  const filteredCandidates = candidates.filter((c) => {
    const nameMatch = c.name.toLowerCase().includes(searchName.toLowerCase());
    const skillMatch =
      searchSkills.length > 0
        ? searchSkills.every((skill) =>
            c.skills.map((s) => s.toLowerCase()).includes(skill.toLowerCase())
          )
        : true;
    return nameMatch && skillMatch;
  });

  // Pagination
  const pageCount = Math.ceil(filteredCandidates.length / PAGE_SIZE);
  const paginatedCandidates = filteredCandidates.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  );

  // Pagination handler (same as Requisitions)
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pageCount) {
      setPage(newPage);
    }
  };

  // For accordion
  const handleAccordionToggle = () => setAccordionOpen((open) => !open);

  // For checkbox - to select multiple users at a time to store the state
  const toggleCandidateSelection = (candidate) => {
    setSelectedCandidates((prev) =>
      prev.some((c) => c.id === candidate.id)
        ? prev.filter((c) => c.id !== candidate.id)
        : [...prev, candidate]
    );
  };

  // After all hooks and logic, do the conditional return
  if (!requisition) return <div>Loading...</div>;

  return (
    <div className="assign-candidates-container">
      <div className="assign-candidates-inner">
        <div className="requisition-accordion">
          <div className="accordion-summary" onClick={handleAccordionToggle}>
            <div>{requisition.jobTitle}</div>
            {/* Accordion */}
            <div
              style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
            >
              <span style={{ color: "#0078B9", fontWeight: "bold" }}>
                {requisition.total_matching || requisition.candidatesCount || 0}{" "}
                profiles matched
                {requisition.availableForAssignment && (
                  <span style={{ color: "#059669", marginLeft: "8px" }}>
                    ({requisition.availableForAssignment} available for
                    assignment)
                  </span>
                )}
              </span>
              <svg
                style={{
                  transform: accordionOpen ? "rotate(90deg)" : "rotate(0deg)",
                  transition: "transform 0.2s",
                }}
                width="20"
                height="20"
                fill="none"
                viewBox="0 0 24 24"
              >
                <path
                  d="M9 6l6 6-6 6"
                  stroke="#0078b9"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>
          {/* About Job Details */}
          {accordionOpen && (
            <div className="accordion-details">
              <div className="accordion-details-grid">
                <div>
                  <span className="accordion-details-label">Job Title:</span>{" "}
                  {requisition.jobTitle}
                </div>
                <div>
                  <span className="accordion-details-label">Department:</span>{" "}
                  {requisition.department}
                </div>
                <div>
                  <span className="accordion-details-label">Location:</span>{" "}
                  {requisition.location || "-"}
                </div>

                {/* <div>
                  <span className="accordion-details-label">Openings:</span>{" "}
                  {requisition.openings}
                </div> */}
                <div>
                  <span className="accordion-details-label">Skills:</span>{" "}
                  {/* {requisition.skills} */}
                  {requisition.skills &&
                  Array.isArray(requisition.skills) &&
                  requisition.skills.length > 0
                    ? requisition.skills.join(", ")
                    : requisition.skills || "-"}
                </div>
              </div>
              {/* <div style={{ marginTop: "0.5rem" }}>
                <span className="accordion-details-label">Description:</span>{" "}
                {requisition.description}
              </div> */}
            </div>
          )}
          {/* For search and skills filter */}
          <div
            className="assign-candidates-filters"
            style={{
              marginBottom: "1.5rem",
              padding: "0.5rem 0",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
              <input
                type="text"
                placeholder="Search by name"
                value={searchName}
                onChange={(e) => {
                  setSearchName(e.target.value);
                  setPage(1);
                }}
                style={{
                  minWidth: 180,
                  maxWidth: 260,
                  padding: "0.4rem 1rem",
                  border: "1.5px solid #d1d5db",
                  borderRadius: 6,
                  background: "#fff",
                  fontSize: "1rem",
                }}
                                
              />
              <Select
                isMulti
                options={skillsOptions.map((s) => ({ value: s, label: s }))}
                value={searchSkills.map((s) => ({ value: s, label: s }))}
                onChange={(selected) => {
                  setSearchSkills(
                    selected ? selected.map((opt) => opt.value) : []
                  );
                  setPage(1);
                }}
                placeholder="Search by skills"
                isSearchable={true}
                classNamePrefix="react-select"
                styles={{
                  container: (base) => ({
                    ...base,
                    minWidth: 300,
                    maxWidth: 340,
                  }),
                  menu: (base) => ({ ...base, zIndex: 9999 }),
                }}
              />
            </div>
          </div>
          {/* To display data in a table */}
          {loading ? (
            <div
              style={{
                textAlign: "center",
                padding: "3rem 0",
                background: "#fff",
                borderRadius: 12,
                boxShadow: "0 2px 16px rgba(0,0,0,0.07)",
              }}
            >
              <img
                src={require("../assets/loader.gif")}
                style={{ width: 50 }}
                alt="Loading..."
              />
              <div style={{ marginTop: "1rem", color: "#6b7280" }}>
                Loading candidates...
              </div>
            </div>
          ) : (
            <div
              className="assign-candidates-table-wrapper"
              style={{
                boxShadow: "0 2px 16px rgba(0,0,0,0.07)",
                borderRadius: 12,
                background: "#fff",
              }}
            >
              <table
                className="assign-candidates-table"
                style={{ width: "100%", borderCollapse: "collapse" }}
              >
                <thead>
                  <tr style={{ background: "#f8fafc" }}>
                    {/* <th
                    style={{
                      padding: "16px",
                      fontWeight: 600,
                      color: "#4a5568",
                      fontSize: 14,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      borderBottom: "1px solid #f7fafc",
                    }}
                  >
                    {" "}
                  </th> */}
                    <th style={{ padding: "16px" }}>Name</th>
                    <th style={{ padding: "16px" }}>Email</th>
                    <th style={{ padding: "16px" }}>Phone</th>
                    <th style={{ padding: "16px" }}>Skills</th>
                    <th style={{ padding: "16px" }}>Resume</th>
                    <th style={{ padding: "16px" }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedCandidates.length === 0 ? (
                    <tr>
                      <td
                        colSpan={10}
                        style={{ textAlign: "center", padding: "2rem 0" }}
                      >
                        No candidates found
                      </td>
                    </tr>
                  ) : (
                    paginatedCandidates.map((candidate) => {
                      const isSelected = selectedCandidates.some(
                        (c) => c.id === candidate.id
                      );
                      return (
                        <tr
                          key={candidate.id}
                          className={isSelected ? "selected" : ""}
                          style={{
                            background: isSelected ? "#e6f0fa" : "#fff",
                            transition: "background 0.2s",
                            cursor: "pointer",
                          }}
                        >
                          {/* <td style={{ padding: "12px 16px" }}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleCandidateSelection(candidate)}
                            style={{ width: 18, height: 18, cursor: "pointer" }}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </td> */}
                          <td style={{ padding: "12px 16px", fontWeight: 500 }} title={candidate.fullName || candidate.name}>
                            {candidate.fullName || candidate.name}
                          </td>
                          <td style={{ padding: "12px 16px" }} title={candidate.email}>
                            {candidate.email}
                          </td>
                          <td style={{ padding: "12px 16px" }} title={candidate.phone || "-"}>
                            {candidate.phone || "-"}
                          </td>

                          <td style={{ padding: "12px 16px" }} title={candidate.skills && candidate.skills.length > 0 ? candidate.skills.join(", ") : "-"}>
                            {candidate.skills && candidate.skills.length > 0
                              ? candidate.skills.join(", ")
                              : "-"}
                          </td>
                          <td style={{ padding: "12px 16px" }}>
                            {candidate.resumeUrl ? (
                              <a
                                href={candidate.resumeUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                title="View Resume"
                              >
                                <AiOutlineEye
                                  size={22}
                                  color="#0078b9"
                                  style={{ verticalAlign: "middle" }}
                                />
                              </a>
                            ) : (
                              "-"
                            )}
                          </td>
                          {/* {roleId !== "4" && ( */}
                          <td style={{ padding: "12px 16px" }}>
                            <div className="d-flex align-items-center">
                              <button
                                className="assign-btn"
                                style={{
                                  padding: "0.4rem 1rem",
                                  fontSize: "0.95rem",
                                  minWidth: 120,
                                  background: "#0078B9",
                                  color: "#fff",
                                  border: "none",
                                  fontWeight: 600,
                                  transition: "all 0.2s",
                                }}
                                onClick={async (e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  const userId = localStorage.getItem("user_id");
                                  console.log("requistion", requisition);
                                  // Assign single candidate only
                                  const payload = {
                                    requisition_id:
                                      location.state?.jobId || requisition?.id,
                                    requisition_correl_id: requisition?.correl_id,
                                    candidate_ids: [candidate.id],
                                    assigned_by: userId ? parseInt(userId) : 1,
                                  };
                                  try {
                                    Swal.fire({
                                      html: `<img src=${require("../assets/loader.gif")} style="width: 50px" alt="loader">`,
                                      showConfirmButton: false,
                                      heightAuto: false,
                                      customClass: { popup: "loader-swal" },
                                      allowOutsideClick: false,
                                    });
                                    const res = await assignCandidates(payload);
                                    Swal.close();
                                    if (res && res.status === "S") {
                                      await fetchCandidatesData();
                                      Swal.fire({
                                        icon: "success",
                                        title: "Candidate submitted successfully",
                                        confirmButtonColor: "#0078b9",
                                        confirmButtonText: "OK",
                                      });
                                    } else {
                                      Swal.fire({
                                        icon: "error",
                                        title:
                                          res?.message || "Assignment failed!",
                                        confirmButtonColor: "#0078b9",
                                      });
                                    }
                                  } catch (err) {
                                    Swal.close();
                                    Swal.fire({
                                      icon: "error",
                                      title: "Assignment failed!",
                                      confirmButtonColor: "#0078b9",
                                    });
                                  }
                                }}
                              >
                                Submit
                              </button>
                            </div>
                          </td>
                          {/* )} */}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
              {/* Pagination */}
              {pageCount > 1 && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "0.5rem",
                    padding: "1rem",
                    borderTop: "1px solid #e5e7eb",
                  }}
                >
                  <button
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page === 1}
                    style={{
                      padding: "0.5rem 1rem",
                      background: page === 1 ? "#f3f4f6" : "#0078B9",
                      color: page === 1 ? "#9ca3af" : "#fff",
                      border: "none",
                      borderRadius: "4px",
                      cursor: page === 1 ? "not-allowed" : "pointer",
                      fontSize: "0.875rem",
                    }}
                  >
                    Previous
                  </button>
                  <span style={{ fontSize: "0.875rem", color: "#6b7280" }}>
                    Page {page} of {pageCount}
                  </span>
                  <button
                    onClick={() => handlePageChange(page + 1)}
                    disabled={page === pageCount}
                    style={{
                      padding: "0.5rem 1rem",
                      background: page === pageCount ? "#f3f4f6" : "#0078B9",
                      color: page === pageCount ? "#9ca3af" : "#fff",
                      border: "none",
                      borderRadius: "4px",
                      cursor: page === pageCount ? "not-allowed" : "pointer",
                      fontSize: "0.875rem",
                    }}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
    // </div>
  );
}

export default AssignCandidate;
